/**
 * useOrganizationSettings Hook
 * Provides access to organization settings with update capability
 */

import { useAtom, useSetAtom } from "jotai";
import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { organizationSettingsQueryAtom } from "@/store/adminDataStore";
import { userStateAtom } from "@/store/userStore";
import { adminAPI } from "@/services";
import type { IOrganizationSettings } from "@/services";
import { invalidateWithTimeout } from "@/utils/queryUtils";

export interface UseOrganizationSettingsReturn {
  /** Current organization settings */
  settings: IOrganizationSettings | null;
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Refetch function */
  refetch: () => void;
  /** Update organization settings */
  updateSettings: (
    data: IOrganizationSettings,
  ) => Promise<IOrganizationSettings | null>;
  /** Updating state */
  isUpdating: boolean;
  /** Update error */
  updateError: Error | null;
}

export function useOrganizationSettings(): UseOrganizationSettingsReturn {
  const [query] = useAtom(organizationSettingsQueryAtom);
  const setUserState = useSetAtom(userStateAtom);
  const queryClient = useQueryClient();
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<Error | null>(null);

  const settings = query.data ?? null;

  // Update settings mutation
  const updateSettings = useCallback(
    async (
      data: IOrganizationSettings,
    ): Promise<IOrganizationSettings | null> => {
      // Validate hours_in_day is within valid range
      if (data.hours_in_day < 1 || data.hours_in_day > 24) {
        setUpdateError(new Error("Hours in day must be between 1 and 24"));
        return null;
      }

      setIsUpdating(true);
      setUpdateError(null);

      try {
        const result = await adminAPI.updateOrganizationSettings(data);
        // Invalidate and refetch organization settings
        await invalidateWithTimeout(queryClient, ["organizationSettings"]);
        // Also update userStateAtom to keep hoursInDayAtom in sync (Bug #9020)
        setUserState((prev) =>
          prev ? { ...prev, organization_settings: result } : prev,
        );
        return result;
      } catch (error) {
        setUpdateError(error as Error);
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [queryClient, setUserState],
  );

  return {
    settings,
    isLoading: query.isLoading,
    error: query.error ?? null,
    refetch: query.refetch,
    updateSettings,
    isUpdating,
    updateError,
  };
}
